"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.pasteSetFields = void 0;
const command_manager_1 = require("../../command_manager");
const model_1 = require("../../model");
const store_1 = require("../../exports/store");
const selectors_1 = require("../../exports/store/selectors");
const types_1 = require("../../types");
const utils_1 = require("../../utils");
const __1 = require("..");
const add_fields_1 = require("./add_fields");
const set_field_attr_1 = require("./set_field_attr");
exports.pasteSetFields = {
    undoable: true,
    execute: (context, options) => {
        const { model: state } = context;
        const { viewId, column, stdValues } = options;
        const { fields } = options;
        const datasheetId = store_1.Selectors.getActiveDatasheetId(state);
        const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
        if (!snapshot) {
            return null;
        }
        const view = (0, selectors_1.getViewById)(snapshot, viewId);
        const actions = [];
        const linkedActions = [];
        // Currently only the Grid view has paste
        if (!view || ![store_1.ViewType.Grid, store_1.ViewType.Gantt].includes(view.type)) {
            return null;
        }
        if (isNaN(column) || column < 0) {
            return null;
        }
        const fieldCount = fields.length;
        const visibleColumns = (0, selectors_1.getVisibleColumns)(state);
        const columnsToPaste = visibleColumns.slice(column, column + fieldCount);
        if (columnsToPaste.length === 0) {
            return null;
        }
        const fieldMap = snapshot.meta.fieldMap;
        const { fieldPropertyEditable, fieldCreatable } = store_1.Selectors.getPermissions(state);
        function enrichColumnProperty(column, stdValues) {
            const oldField = fieldMap[column.fieldId];
            if (!fieldPropertyEditable) {
                return;
            }
            const newField = (0, utils_1.fastCloneDeep)(oldField);
            if (newField.type === types_1.FieldType.Member) {
                return;
            }
            const newProperty = model_1.Field.bindContext(newField, state).enrichProperty(stdValues);
            if (newProperty === newField.property) {
                return;
            }
            const rst = set_field_attr_1.setFieldAttr.execute(context, {
                cmd: __1.CollaCommandName.SetFieldAttr,
                fieldId: column.fieldId,
                data: Object.assign(Object.assign({}, newField), { property: newProperty }),
            });
            if (rst && rst.result === command_manager_1.ExecuteResult.Success) {
                actions.push(...rst.actions);
                rst.linkedActions && linkedActions.push(...rst.linkedActions);
            }
        }
        // In the case where only one cell is copied, enrich field property on the selection column
        const singleCellPaste = stdValues.length === 1 && stdValues[0].length === 1;
        if (singleCellPaste) {
            const ranges = (0, selectors_1.getSelectRanges)(state);
            const range = ranges[0];
            const fields = store_1.Selectors.getRangeFields(state, range, datasheetId);
            let stdValue = stdValues[0][0];
            const data = stdValue.data.filter(d => d.text);
            stdValue = Object.assign(Object.assign({}, stdValue), { data });
            for (const field of fields) {
                enrichColumnProperty({ fieldId: field.id }, [stdValue]);
            }
        }
        else {
            for (let i = 0, ii = columnsToPaste.length; i < ii; i++) {
                const column = columnsToPaste[i];
                const stdValueField = stdValues.reduce((result, stdValueRow) => {
                    const stdValue = stdValueRow[i];
                    if (stdValue) {
                        const data = stdValue.data.filter(d => d.text);
                        result.push(Object.assign(Object.assign({}, stdValue), { data }));
                    }
                    return result;
                }, []);
                enrichColumnProperty(column, stdValueField);
            }
        }
        // have columns to expand
        let newFields = fields.slice(columnsToPaste.length);
        // Does not allow adding new columns without the fieldCreatable permission
        if (fieldCreatable && newFields.length > 0) {
            const fieldNames = new Set();
            for (const fieldId in fieldMap) {
                fieldNames.add(fieldMap[fieldId].name);
            }
            newFields = newFields.map(field => {
                const originName = field.name;
                // The incoming fields may have fieldId. In order to ensure that it does not conflict with the id of the newly created field, 
                // we force it to be deleted in this category.
                delete field.id;
                let name = originName;
                let i = 1;
                if (!originName) {
                    do {
                        name = `${utils_1.NamePrefix.Field} ${i++}`;
                    } while (fieldNames.has(name));
                }
                else {
                    while (fieldNames.has(name)) {
                        name = `${originName} (${i++})`;
                    }
                }
                fieldNames.add(name);
                if (field.type === types_1.FieldType.LookUp) {
                    const relatedLinkFieldId = field.property.relatedLinkFieldId;
                    if (!fieldMap[relatedLinkFieldId] || fieldMap[relatedLinkFieldId].type !== types_1.FieldType.Link) {
                        return {
                            name,
                            type: types_1.FieldType.Text,
                            property: model_1.TextField.defaultProperty(),
                        };
                    }
                }
                return Object.assign(Object.assign({}, field), { name });
            });
            const rst = add_fields_1.addFields.execute(context, {
                cmd: __1.CollaCommandName.AddFields,
                data: newFields.map((field, index) => ({
                    viewId: view.id,
                    index: index + visibleColumns.length,
                    data: field,
                })),
            });
            if (rst) {
                if (rst.result === command_manager_1.ExecuteResult.Fail) {
                    return rst;
                }
                actions.push(...rst.actions);
                rst.linkedActions && linkedActions.push(...rst.linkedActions);
            }
        }
        if (actions.length === 0) {
            return null;
        }
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            actions,
            linkedActions,
        };
    },
};
/*

 declare module '../../command_manager/command_manager' {
 interface CollaCommandManager {
 execute(options: IPasteSetFieldsOptions & { cmd: 'PasteSetFields' }):
 ICollaCommandExecuteResult<IPasteSetFieldsOptions>;
 }
 }

 */
//# sourceMappingURL=paste_set_fields.js.map